import ReactDOM from "react-dom/client";
import React, { useState, useEffect } from "react";

type Resolution = [string, string];

function App() {
	const [alias, setAlias] = useState("");
	const [results, setResults] = useState<Resolution[]>([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const [bulkInput, setBulkInput] = useState("");
	const [bulkLoading, setBulkLoading] = useState(false);
	const [bulkResults, setBulkResults] = useState<
		Record<string, Resolution[]>
	>({});

	useEffect(() => {
		const value = alias.trim();

		if (!value) {
			setResults([]);
			setError(null);
			return;
		}

		const controller = new AbortController();

		const timeout = setTimeout(async () => {
			try {
				setLoading(true);
				setError(null);

				const response = await fetch(
					`/resolve?kuerzel=${encodeURIComponent(value)}`,
					{ signal: controller.signal },
				);

				if (!response.ok) {
					throw new Error(`Request failed (${response.status})`);
				}

				const data: Resolution[] = await response.json();
				setResults(data);
			} catch (err) {
				if ((err as Error).name !== "AbortError") {
					setError((err as Error).message);
					setResults([]);
				}
			} finally {
				setLoading(false);
			}
		}, 300);

		return () => {
			clearTimeout(timeout);
			controller.abort();
		};
	}, [alias]);

	async function resolveBulk() {
		const aliases = bulkInput
			.split(/\r?\n/)
			.map((x) => x.trim())
			.filter(Boolean);

		if (aliases.length === 0) {
			setBulkResults({});
			return;
		}

		setBulkLoading(true);

		try {
			const entries = await Promise.all(
				aliases.map(async (a) => {
					const response = await fetch(
						`/resolve?kuerzel=${encodeURIComponent(a)}`,
					);

					if (!response.ok) {
						throw new Error(
							`${a}: Request failed (${response.status})`,
						);
					}

					const data: Resolution[] = await response.json();
					return [a, data] as const;
				}),
			);

			setBulkResults(Object.fromEntries(entries));
		} finally {
			setBulkLoading(false);
		}
	}

	return (
		<div
			style={{
				maxWidth: 900,
				margin: "2rem auto",
				fontFamily: "sans-serif",
			}}
		>
			<h1>HSG Alias Resolver</h1>

			<h2>Single Lookup</h2>

			<input
				type="text"
				placeholder="Alias eingeben..."
				value={alias}
				onChange={(e) => setAlias(e.target.value)}
				style={{
					width: "calc(100% - 1rem)",
					padding: "0.5rem",
					fontSize: "1rem",
				}}
			/>

			{loading && <p>Searching…</p>}
			{error && <p style={{ color: "red" }}>{error}</p>}

			{results.length > 0 && (
				<table
					style={{
						width: "100%",
						borderCollapse: "collapse",
						marginTop: "1rem",
					}}
				>
					<thead>
						<tr>
							<th style={{ textAlign: "left" }}>Vorname</th>
							<th style={{ textAlign: "left" }}>Nachname</th>
						</tr>
					</thead>
					<tbody>
						{results.map(([vorname, nachname]) => (
							<tr key={`${vorname}-${nachname}`}>
								<td>{vorname}</td>
								<td>{nachname}</td>
							</tr>
						))}
					</tbody>
				</table>
			)}

			<hr style={{ margin: "2rem 0" }} />

			<h2>Bulk Lookup</h2>

			<p>Ein Kürzel pro Zeile:</p>

			<textarea
				value={bulkInput}
				onChange={(e) => setBulkInput(e.target.value)}
				rows={10}
				style={{
					width: "100%",
					fontFamily: "monospace",
				}}
				placeholder={`30MuMa`}
			/>

			<div style={{ marginTop: "1rem" }}>
				<button onClick={resolveBulk} disabled={bulkLoading}>
					{bulkLoading ? "Resolving..." : "Resolve List"}
				</button>
			</div>
			<table style={{ borderSpacing: "5px" }}>
				{Object.keys(bulkResults).length > 0 && (
					<thead>
						<tr>
							<th>Alias</th>
							<th>Matches</th>
						</tr>
					</thead>
				)}
				<tbody>
					{Object.entries(bulkResults).map(([alias, matches]) => (
						<tr key={alias}>
							<td style={{ verticalAlign: "top" }}>{alias}</td>
							<td>
								{matches.length === 0 ? (
									<div>No matches</div>
								) : (
									matches.map(([vorname, nachname]) => (
										<div
											key={`${alias}-${vorname}-${nachname}`}
										>
											<span>{`${vorname} ${nachname}`}</span>
										</div>
									))
								)}
							</td>
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
}

ReactDOM.createRoot(document.getElementById("app")!).render(<App />);
